import React, { useEffect, useState } from "react";
import { IconDownload, IconRefresh, IconX } from "@tabler/icons-react";

/**
 * PWA manager:
 * - Registra el service worker en producción/HTTPS.
 * - Escucha 'beforeinstallprompt' → muestra un botón nativo "Instalar" (Android/Chrome).
 * - Cuando el SW nuevo se activa → muestra toast "Nueva versión disponible" con botón refrescar.
 * - En iOS no hay 'beforeinstallprompt'; mostramos instrucción sutil "Añadir a pantalla de inicio" 1 sola vez.
 */
export default function PWAManager() {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    // Standalone detection
    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (isStandalone) setInstalled(true);

    // Register service worker
    if ("serviceWorker" in navigator && window.location.protocol === "https:") {
      window.addEventListener("load", () => {
        navigator.serviceWorker.register("/sw.js").catch(() => { /* ignore */ });
      });
      navigator.serviceWorker.addEventListener("message", (event) => {
        if (event.data?.type === "SW_UPDATED") {
          setUpdateAvailable(true);
        }
      });
      // Also detect controller change
      let refreshing = false;
      navigator.serviceWorker.addEventListener("controllerchange", () => {
        if (refreshing) return;
        refreshing = true;
        // First install shouldn't force reload; only subsequent updates
      });
    }

    // Android/Chrome install prompt
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS hint (Safari + not already dismissed)
    const isIos = /iphone|ipad|ipod/i.test(window.navigator.userAgent);
    const isSafari = /^((?!chrome|android).)*safari/i.test(window.navigator.userAgent);
    const dismissed = localStorage.getItem("chiessa.ios.install.dismissed");
    if (isIos && isSafari && !isStandalone && !dismissed) {
      // Delay so it doesn't appear on cold visit
      const t = setTimeout(() => setIosHint(true), 15000);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      };
    }
    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, []);

  const install = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === "accepted") setInstalled(true);
    setInstallPrompt(null);
  };

  const refresh = () => {
    // Ask the waiting SW to activate immediately, then reload the page
    navigator.serviceWorker.getRegistration().then((reg) => {
      if (reg?.waiting) reg.waiting.postMessage({ type: "SKIP_WAITING" });
      window.location.reload();
    });
  };

  const dismissIos = () => {
    localStorage.setItem("chiessa.ios.install.dismissed", "1");
    setIosHint(false);
  };

  return (
    <>
      {/* Install button (Android/Chrome) */}
      {installPrompt && !installed && (
        <div
          className="fixed bottom-4 right-4 z-40 bg-teal-deep text-white rounded-xl shadow-2xl p-4 max-w-[340px]"
          data-testid="pwa-install-card"
        >
          <p className="font-display text-[16px] mb-1">Instala Chiessa en tu celular</p>
          <p className="text-[12.5px] opacity-85 leading-snug mb-3">
            Abre más rápido, funciona pantalla completa y aparece como app en tu pantalla de inicio.
          </p>
          <div className="flex gap-2">
            <button
              onClick={install}
              className="bg-honey text-white rounded-lg px-3 py-2 text-[12.5px] font-medium flex items-center gap-1"
              data-testid="pwa-install-btn"
            >
              <IconDownload size={14} /> Instalar
            </button>
            <button
              onClick={() => setInstallPrompt(null)}
              className="text-white/70 hover:text-white text-[12px] px-2"
              aria-label="Cerrar"
            >
              Después
            </button>
          </div>
        </div>
      )}

      {/* iOS hint */}
      {iosHint && !installed && (
        <div
          className="fixed bottom-4 left-4 right-4 z-40 bg-paper border border-line rounded-xl shadow-2xl p-4 max-w-[380px] mx-auto"
          data-testid="pwa-ios-hint"
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="font-display text-[15px] text-teal-deep">Instala Chiessa como app</p>
              <p className="text-[12.5px] text-muted leading-relaxed mt-1">
                Toca <b>Compartir</b> en Safari y elige <b>&quot;Añadir a pantalla de inicio&quot;</b>.
              </p>
            </div>
            <button onClick={dismissIos} className="text-tert p-1" aria-label="Cerrar">
              <IconX size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Update available toast */}
      {updateAvailable && (
        <div
          className="fixed top-16 right-4 z-40 bg-honey text-white rounded-xl shadow-xl px-4 py-3 flex items-center gap-3"
          data-testid="pwa-update-toast"
        >
          <IconRefresh size={16} />
          <p className="text-[13px]">Nueva versión disponible</p>
          <button
            onClick={refresh}
            className="bg-white/20 hover:bg-white/30 rounded-lg px-3 py-1 text-[12px] font-medium"
            data-testid="pwa-update-btn"
          >
            Refrescar
          </button>
        </div>
      )}
    </>
  );
}
